import { DeleteOutlined } from "@ant-design/icons";
import cuid from "cuid";
import { FieldArray } from "formik";
import { get, max, sortBy, uniq } from "lodash";
import { Fragment } from "react";
import { BiPlus } from "react-icons/bi";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { PROCESS_OPTIONS } from "utils/api/graphql/queries/processes";
import { STORES } from "utils/api/graphql/queries/stores";
import { SKILL_TYPES } from "utils/constants";
import { bindArrayInputProps } from "utils/helpers/input";
import SkillDepartmentField from "./SkillDepartmentField";
import { handleSkillTypeChange } from "./skillTypeOnChange";
import { getSkillGroupIndex } from "./userSkills";

const SkillGroups = ({ formProps, FORM }) => {
  const skills = get(formProps, "values.skills", []);
  const groupIndexes = sortBy(uniq(skills.map(getSkillGroupIndex)));

  const bindValue = (index) =>
    bindArrayInputProps({
      parent: "skills",
      index,
      name: "value",
      ...formProps,
    });

  const InputSwitchSkill = (index) => ({
    [SKILL_TYPES.AGENCY]: (
      <Input
        placeholder={FORM.SKILLS.VALUE}
        label={FORM.SKILLS.VALUE}
        {...bindValue(index)}
      />
    ),
    [SKILL_TYPES.ESTABLISHMENT]: (
      <Input
        placeholder={FORM.SKILLS.VALUE}
        label={FORM.SKILLS.VALUE}
        {...bindValue(index)}
      />
    ),
    [SKILL_TYPES.ZIPCODE]: (
      <Input
        placeholder={FORM.SKILLS.VALUE}
        label={FORM.SKILLS.VALUE}
        {...bindValue(index)}
      />
    ),
    [SKILL_TYPES.STORE]: (
      <Select
        type="async"
        node={{
          query: STORES,
          variables: {},
          accessors: {
            root: "stores.data",
          },
        }}
        placeholder={FORM.SKILLS.VALUE}
        label={FORM.SKILLS.VALUE}
        {...bindValue(index)}
      />
    ),
    [SKILL_TYPES.PROCESS]: (
      <Select
        type="async"
        node={{
          query: PROCESS_OPTIONS,
          variables: {},
          accessors: {
            root: "processes.data",
          },
        }}
        placeholder={FORM.SKILLS.VALUE}
        label={FORM.SKILLS.VALUE}
        {...bindValue(index)}
      />
    ),
    [SKILL_TYPES.DEPARTMENT]: (
      <SkillDepartmentField
        formProps={formProps}
        index={index}
        excludeLabel={get(FORM, "SKILLS.EXCLUDE", "Exclure")}
        valueLabel={FORM.SKILLS.VALUE}
      />
    ),
  });

  const newSkill = (groupIndex) => ({
    id: cuid(),
    type: null,
    value: null,
    groupIndex,
  });

  const removeGroup = (groupIndex) =>
    formProps.setFieldValue(
      "skills",
      skills.filter((skill) => getSkillGroupIndex(skill) !== groupIndex),
    );

  const typeOptions = Object.entries(SKILL_TYPES).map(([key, value]) => ({
    label: get(FORM, `SKILLS.TYPES.${key}`, key),
    value,
  }));

  return (
    <Card title={FORM.SKILLS.TITLE} divider={false} key="skills">
      <FieldArray
        name={"skills"}
        render={(arrayHelpers) => (
          <div className="skill-groups">
            {groupIndexes.map((groupIndex, position) => {
              const groupRows = skills
                .map((skill, index) => ({ skill, index }))
                .filter(
                  ({ skill }) => getSkillGroupIndex(skill) === groupIndex,
                );

              return (
                <Fragment key={`group-${groupIndex}`}>
                  {position > 0 && (
                    <div className="skill-groups__or">{FORM.SKILLS.OR}</div>
                  )}
                  <div className="skill-groups__group">
                    <div className="skill-groups__header">
                      <h4 className="skill-groups__title">
                        {`${FORM.SKILLS.GROUP} ${position + 1}`}
                      </h4>
                      <Button
                        onClick={() => removeGroup(groupIndex)}
                        type="danger--link"
                        icon={<DeleteOutlined />}
                      />
                    </div>
                    {groupRows.map(({ skill, index }, rowPosition) => {
                      const skillType = get(skill, "type");

                      return (
                        <Fragment key={`row-${skill.id || index}`}>
                          {rowPosition > 0 && (
                            <span className="skill-groups__and">
                              {FORM.SKILLS.AND}
                            </span>
                          )}
                          <div className="skill-groups__row">
                            <Select
                              options={typeOptions}
                              placeholder={FORM.SKILLS.TYPE}
                              label={FORM.SKILLS.TYPE}
                              value={skillType}
                              onChange={(value) =>
                                handleSkillTypeChange(formProps, index, value)
                              }
                            />
                            <div>{InputSwitchSkill(index)[skillType]}</div>
                            <Button
                              onClick={() => arrayHelpers.remove(index)}
                              type="danger--outlined"
                              icon={<DeleteOutlined />}
                            />
                          </div>
                        </Fragment>
                      );
                    })}
                    <div className="skill-groups__actions">
                      <Button
                        onClick={() => arrayHelpers.push(newSkill(groupIndex))}
                        type="primary--outlined"
                        icon={<BiPlus size={18} />}
                      >
                        {FORM.SKILLS.ADD_CONDITION}
                      </Button>
                    </div>
                  </div>
                </Fragment>
              );
            })}
            <div className="skill-groups__actions">
              <Button
                onClick={() =>
                  arrayHelpers.push(
                    newSkill(groupIndexes.length ? max(groupIndexes) + 1 : 0),
                  )
                }
                type="primary"
                icon={<BiPlus size={18} />}
              >
                {FORM.SKILLS.ADD_GROUP}
              </Button>
            </div>
          </div>
        )}
      />
    </Card>
  );
};

export default SkillGroups;
